// COLLECT-FLOWS: Setup › Structure › Collection (`/c/{slug}/setup/collection`). What each person
// still owes their agents (their collection session's progress and missing questions) and what
// each role on the work chart still waits for (have / partial / missing: the role readiness
// status). Managers send a session from here (a modal); anyone else sees only their own rows.
// No sample rows: a company with nothing sent shows an honest empty state.
import { COLLECT_FLOWS } from "@companyos/domain-work/flows";
import type { OperationOutput } from "@companyos/sdk";
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  Field,
  FieldLabel,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  Progress,
  QuickAddDialog,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ListChecksIcon, SendIcon } from "lucide-react";
import { useRef, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import { peopleQuery, seatsQuery } from "../queries";
import { SetupPage } from "../settings/setup-page";
import { FormError } from "../structure/parts";
import { flowKeys, openRunOf } from "./flow-queries";

type Collection = OperationOutput<"flows.collection">;
type Status = Collection["roles"][number]["status"];

const STATUS: Record<Status, { label: string; variant: "success" | "warning" | "danger" }> = {
  have: { label: "Have", variant: "success" },
  partial: { label: "Partial", variant: "warning" },
  missing: { label: "Missing", variant: "danger" },
};

const SESSION_LABEL = Object.fromEntries(
  COLLECT_FLOWS.map((d) => [d.key, d.title.replace(/ collection$/, "")]),
) as Record<string, string>;

export const collectionQuery = (api: Api, co: string) => ({
  queryKey: [...flowKeys.all(co), "collection"] as const,
  queryFn: () => api.client.flows.collection({ params: { co } }),
});

export function CollectionScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const q = useQuery(collectionQuery(api, co));
  const [sending, setSending] = useState(false);
  const manager = isManager(company);
  const data = q.data;
  return (
    <SetupPage
      title="Collection"
      icon={ListChecksIcon}
      slot="collection"
      {...(data ? { count: data.people.length } : {})}
      actions={
        manager ? (
          <Button size="sm" onClick={() => setSending(true)}>
            <SendIcon aria-hidden={true} />
            Send session
          </Button>
        ) : null
      }
    >
      {q.isPending ? (
        <p className="text-faint text-sm">Loading…</p>
      ) : q.error ? (
        <FormError what="Loading" error={q.error} />
      ) : data ? (
        <>
          <section className="flex flex-col gap-2" data-slot="collection-people">
            <h2 className="flex items-center gap-1.5 text-sm font-medium">
              People
              <InfoTip label="About people">
                Each person's collection session: how much is in, and what their agents still wait
                for.
              </InfoTip>
            </h2>
            {data.people.length === 0 ? (
              <p className="text-faint text-sm" data-slot="collection-empty">
                No session sent yet.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
                {data.people.map((p) => (
                  <li
                    key={p.run_id}
                    data-slot="collection-person"
                    data-flow={p.flow_key}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(6rem,10rem)_auto] items-center gap-3 px-3 py-2 text-sm max-sm:grid-cols-[minmax(0,1fr)_auto]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {p.person_name ?? p.seat_title}
                      </span>
                      <span className="block truncate text-faint text-xs">
                        {SESSION_LABEL[p.flow_key]} · {p.seat_title}
                      </span>
                    </span>
                    <span className="flex items-center gap-2 max-sm:hidden">
                      <Progress value={p.percent} className="w-full" aria-label="Answered" />
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="tabular-nums" data-slot="collection-count">
                        {p.answered}/{p.total}
                      </span>
                      {p.missing.length ? (
                        <InfoTip label={`Missing for ${p.person_name ?? p.seat_title}`}>
                          <span className="flex flex-col gap-0.5">
                            {p.missing.map((m) => (
                              <span key={m.field}>
                                {m.label}
                                {m.required ? " *" : ""}
                                <span className="text-faint"> · {m.used_by.join(", ")}</span>
                              </span>
                            ))}
                          </span>
                        </InfoTip>
                      ) : (
                        <Badge variant="success">Done</Badge>
                      )}
                      <Link
                        to="/c/$slug/flows/$id"
                        params={{ slug: company.slug, id: p.run_id }}
                        className="text-primary-text text-xs hover:underline"
                      >
                        Open
                      </Link>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {manager && data.unsent.length ? (
              <p className="text-faint text-xs" data-slot="collection-unsent">
                Not sent: {data.unsent.map((k) => SESSION_LABEL[k]).join(", ")}
              </p>
            ) : null}
          </section>
          <section className="flex flex-col gap-2" data-slot="collection-roles">
            <h2 className="flex items-center gap-1.5 text-sm font-medium">
              Roles
              <InfoTip label="About roles">
                What each role on the work chart needs from the sessions, counted per seat. This is
                the role readiness status.
              </InfoTip>
            </h2>
            <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
              {data.roles.map((r) => (
                <li
                  key={r.role}
                  data-slot="collection-role"
                  data-role={r.role}
                  data-status={r.status}
                  className="flex items-center gap-3 px-3 py-2 text-sm"
                >
                  <span className="min-w-0 flex-1 truncate">{r.role}</span>
                  <span className="tabular-nums text-faint">
                    {r.answered}/{r.total}
                  </span>
                  <Badge variant={STATUS[r.status].variant}>{STATUS[r.status].label}</Badge>
                  {r.missing.length ? (
                    <InfoTip label={`Missing for ${r.role}`}>
                      <span className="flex flex-col gap-0.5">
                        {r.missing.map((m) => (
                          <span key={`${m.flow_key}:${m.seat_id ?? "unsent"}:${m.field}`}>
                            {m.label}
                            <span className="text-faint">
                              {" "}
                              ·{" "}
                              {m.seat_id
                                ? (m.person_name ?? SESSION_LABEL[m.flow_key])
                                : `${SESSION_LABEL[m.flow_key]} (not sent)`}
                            </span>
                          </span>
                        ))}
                      </span>
                    </InfoTip>
                  ) : (
                    <span className="size-5" />
                  )}
                </li>
              ))}
            </ul>
          </section>
        </>
      ) : null}
      {sending ? (
        <SendCollectionDialog api={api} company={company} onClose={() => setSending(false)} />
      ) : null}
    </SetupPage>
  );
}

/** Send one person their collection session about the seat they hold (a modal). */
function SendCollectionDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [flow, setFlow] = useState("");
  const [seatId, setSeatId] = useState("");
  const [member, setMember] = useState("");
  const seats = useInfiniteQuery(seatsQuery(api, co));
  const people = useInfiniteQuery(peopleQuery(api, co));
  const seatList = (seats.data?.pages ?? [])
    .flatMap((p) => p.data)
    .filter((s) => s.status !== "closed" && s.status !== "proposed");
  const members = (people.data?.pages ?? [])
    .flatMap((p) => p.data)
    .filter((p) => p.kind === "person" && p.status === "active");
  const attempt = useRef<{ sig: string; key: string } | null>(null);
  const keyFor = (sig: string) => {
    if (attempt.current?.sig !== sig) attempt.current = { sig, key: newIdempotencyKey() };
    return attempt.current.key;
  };
  const send = useMutation({
    mutationFn: () =>
      api.client.flows.send({
        params: { co },
        idempotencyKey: keyFor(`${flow}:${seatId}:${member}`),
        body: {
          flow_key: flow as Collection["unsent"][number],
          seat_id: seatId,
          membership_id: member,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowKeys.all(co) });
      toast.add({ title: "Session sent", type: "success" });
      onClose();
    },
  });
  const open = send.error ? openRunOf(send.error) : null;
  return (
    <QuickAddDialog
      title="Send session"
      info="They answer short questions; each answer goes to the agents that use it."
      submitLabel="Send"
      canSubmit={flow !== "" && seatId !== "" && member !== ""}
      pending={send.isPending}
      onSubmit={() => send.mutate()}
      onClose={onClose}
      slot="send-collection"
      error={
        open ? (
          <p className="text-sm">That seat already has this session open.</p>
        ) : (
          <FormError what="Sending" error={send.error} />
        )
      }
    >
      <Field>
        <FieldLabel htmlFor="collect-flow">Session</FieldLabel>
        <NativeSelect id="collect-flow" value={flow} onChange={(e) => setFlow(e.target.value)}>
          <NativeSelectOption value="">Pick a session</NativeSelectOption>
          {COLLECT_FLOWS.map((d) => (
            <NativeSelectOption key={d.key} value={d.key}>
              {SESSION_LABEL[d.key]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <FieldLabel htmlFor="collect-seat">Seat</FieldLabel>
        <NativeSelect id="collect-seat" value={seatId} onChange={(e) => setSeatId(e.target.value)}>
          <NativeSelectOption value="">Pick a seat</NativeSelectOption>
          {seatList.map((s) => (
            <NativeSelectOption key={s.id} value={s.id}>
              {s.title}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <FieldLabel htmlFor="collect-member">Person</FieldLabel>
        <NativeSelect
          id="collect-member"
          value={member}
          onChange={(e) => setMember(e.target.value)}
        >
          <NativeSelectOption value="">Pick the seat's person</NativeSelectOption>
          {members.map((p) => (
            <NativeSelectOption key={p.id} value={p.id}>
              {p.display_name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
    </QuickAddDialog>
  );
}
